'use client';

import { Box, Divider, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { Clipboard } from '../../components/clipboard';
import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Copy01Icon,
  EyeIcon,
  ViewOffIcon,
  Refresh04Icon,
} from '@terra/ui-icons';
import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { MfaMessageDto } from '@terra/shared-contracts-applications';
import { z } from 'zod';

const WebhookProviderMetadataSchema = z.looseObject({
  signatureHeader: z.string().optional().default('x-terra-signature'),
  timestampHeader: z.string().optional().default('x-terra-timestamp'),
  deliveryIdHeader: z.string().optional().default('x-terra-delivery-id'),
  secretLast4: z.string().optional(),
});

interface WebhookDisplayProps {
  applicationId?: string;
  credentialId?: string;
  mfaConfigId?: string;
  providerMetadata?: unknown;
  disabled?: boolean;
  messages?: MfaMessageDto[];
  isLoadingMessages?: boolean;
  onFetchMessages?: () => void;
  onFetchWebhookConfig?: (params: {
    credentialId: string;
    mfaConfigId: string;
  }) => Promise<{
    success: boolean;
    endpointPath?: string;
    signatureHeader?: string;
    timestampHeader?: string;
    deliveryIdHeader?: string;
    secret?: string;
    secretLast4?: string;
    error?: string;
  }>;
}

function buildWebhookEndpointUrl({
  resolvedEndpointPath,
}: {
  resolvedEndpointPath: string | null;
}): string | null {
  if (!resolvedEndpointPath) return null;
  const gatewayBase = process.env.NEXT_PUBLIC_API_GATEWAY_ADDRESS;
  const normalizedGatewayBase = gatewayBase?.replace(/\/$/, '');

  if (normalizedGatewayBase) {
    return `${normalizedGatewayBase}/orchestrator${resolvedEndpointPath}`;
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}/orchestrator${resolvedEndpointPath}`;
  }

  // Server-side / unknown environment fallback: assume gateway is same-origin
  return `/orchestrator${resolvedEndpointPath}`;
}

export function WebhookDisplay({
  applicationId,
  credentialId,
  mfaConfigId,
  providerMetadata,
  disabled = false,
  messages = [],
  isLoadingMessages = false,
  onFetchMessages,
  onFetchWebhookConfig,
}: WebhookDisplayProps) {
  const t = useTranslations('designSystem.mfa.webhookDisplay');
  const tMfa = useTranslations('designSystem.mfa');
  const [isMessagesExpanded, setIsMessagesExpanded] = useState(false);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [endpointPath, setEndpointPath] = useState<string | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const metadata = useMemo(() => {
    const parsed = WebhookProviderMetadataSchema.safeParse(
      providerMetadata ?? {},
    );

    return parsed.success ? parsed.data : null;
  }, [providerMetadata]);
  const resolvedEndpointPath =
    endpointPath ||
    (applicationId && credentialId && mfaConfigId
      ? `/applications/${applicationId}/credentials/${credentialId}/mfa/${mfaConfigId}/webhook`
      : null);
  const resolvedEndpointUrl = buildWebhookEndpointUrl({ resolvedEndpointPath });
  const handleLoadConfig = async () => {
    if (!credentialId || !mfaConfigId || !onFetchWebhookConfig) return;
    setIsLoadingConfig(true);
    setConfigError(null);

    try {
      const result = await onFetchWebhookConfig({
        credentialId,
        mfaConfigId,
      });

      if (!result.success) {
        setConfigError(result.error || t('failedToLoadConfig'));

        return;
      }

      setEndpointPath(result.endpointPath || null);
      setSecret(result.secret || null);
      setIsSecretVisible(true);
    } catch (e) {
      setConfigError(e instanceof Error ? e.message : t('failedToLoadConfig'));
    } finally {
      setIsLoadingConfig(false);
    }
  };

  return (
    <VStack alignItems="stretch" gap={4}>
      <VStack alignItems="stretch" gap={2}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            {t('endpoint')}
          </Text>
          {disabled && (
            <Text fontSize="sm" color="fg.muted">
              {tMfa('disabled')}
            </Text>
          )}
        </HStack>

        <Box
          p={4}
          borderRadius="lg"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.muted"
        >
          <HStack justifyContent="space-between" alignItems="center" gap={3}>
            <Text
              fontSize="sm"
              fontWeight="medium"
              fontFamily="mono"
              color={disabled ? 'fg.disabled' : 'fg.default'}
              wordBreak="break-all"
            >
              {disabled ? '••••••••••••••••••••' : resolvedEndpointUrl || '—'}
            </Text>
            <Clipboard.Root value={resolvedEndpointUrl || ''}>
              <Clipboard.Trigger asChild>
                <IconButton
                  variant="outline"
                  size="sm"
                  disabled={!resolvedEndpointUrl || disabled}
                >
                  <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                    <Copy01Icon size={16} />
                  </Clipboard.Indicator>
                </IconButton>
              </Clipboard.Trigger>
            </Clipboard.Root>
          </HStack>
        </Box>

        <Text fontSize="xs" color="fg.muted">
          {t('instructions')}
        </Text>
      </VStack>

      <VStack alignItems="stretch" gap={2}>
        <Text fontSize="sm" fontWeight="medium" color="fg.default">
          {t('requiredHeaders')}
        </Text>
        <Box
          p={4}
          borderRadius="lg"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.muted"
        >
          <VStack alignItems="stretch" gap={2}>
            <Text fontSize="xs" fontFamily="mono" color="fg.muted">
              {t('timestampHeaderExample', {
                header: metadata?.timestampHeader || 'x-terra-timestamp',
              })}
            </Text>
            <Text fontSize="xs" fontFamily="mono" color="fg.muted">
              {t('signatureHeaderExample', {
                header: metadata?.signatureHeader || 'x-terra-signature',
              })}
            </Text>
            <Text fontSize="xs" fontFamily="mono" color="fg.muted">
              {t('deliveryIdHeaderExample', {
                header: metadata?.deliveryIdHeader || 'x-terra-delivery-id',
              })}
            </Text>
          </VStack>
        </Box>
      </VStack>

      <VStack alignItems="stretch" gap={2}>
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            {t('signingSecret')}
          </Text>
          {onFetchWebhookConfig && credentialId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLoadConfig}
              disabled={disabled || isLoadingConfig}
              aria-label={t('loadConfig')}
            >
              <Refresh04Icon
                size={14}
                style={{
                  animation: isLoadingConfig
                    ? 'spin 1s linear infinite'
                    : 'none',
                }}
              />
              {isLoadingConfig ? t('loading') : t('load')}
            </Button>
          )}
        </HStack>

        <Box
          p={4}
          borderRadius="lg"
          bg="bg.muted"
          borderWidth="1px"
          borderColor="border.muted"
        >
          <HStack justifyContent="space-between" alignItems="center" gap={3}>
            <Text
              fontSize="sm"
              fontFamily="mono"
              color="fg.default"
              wordBreak="break-all"
            >
              {disabled
                ? '••••••••••••••••'
                : secret
                  ? isSecretVisible
                    ? secret
                    : '••••••••••••••••'
                  : metadata?.secretLast4
                    ? t('secretEndsWith', { last4: metadata.secretLast4 })
                    : '—'}
            </Text>

            <HStack gap={2}>
              {secret && (
                <IconButton
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSecretVisible((v) => !v)}
                  aria-label={
                    isSecretVisible ? t('hideSecret') : t('showSecret')
                  }
                  disabled={disabled}
                >
                  {isSecretVisible ? (
                    <ViewOffIcon size={16} />
                  ) : (
                    <EyeIcon size={16} />
                  )}
                </IconButton>
              )}
              <Clipboard.Root value={secret || ''}>
                <Clipboard.Trigger asChild>
                  <IconButton
                    variant="outline"
                    size="sm"
                    disabled={!secret || disabled}
                  >
                    <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                      <Copy01Icon size={16} />
                    </Clipboard.Indicator>
                  </IconButton>
                </Clipboard.Trigger>
              </Clipboard.Root>
            </HStack>
          </HStack>
        </Box>

        {configError && (
          <Text fontSize="xs" color="red.10">
            {configError}
          </Text>
        )}
      </VStack>

      {onFetchMessages && (
        <>
          <Divider />
          <VStack alignItems="stretch" gap={3}>
            <HStack justifyContent="space-between" alignItems="center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMessagesExpanded(!isMessagesExpanded)}
                aria-label={
                  isMessagesExpanded ? t('hideMessages') : t('showMessages')
                }
              >
                <HStack gap={2} alignItems="center">
                  <Text fontSize="sm" fontWeight="medium" color="fg.default">
                    {t('latestMessages')}
                  </Text>
                  {isMessagesExpanded ? (
                    <ArrowUp01Icon size={16} />
                  ) : (
                    <ArrowDown01Icon size={16} />
                  )}
                </HStack>
              </Button>
              {isMessagesExpanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onFetchMessages}
                  disabled={isLoadingMessages || disabled}
                  aria-label={t('refreshMessages')}
                >
                  <Refresh04Icon
                    size={14}
                    style={{
                      animation: isLoadingMessages
                        ? 'spin 1s linear infinite'
                        : 'none',
                    }}
                  />
                  {t('refresh')}
                </Button>
              )}
            </HStack>

            {isMessagesExpanded && (
              <Box
                p={4}
                borderRadius="lg"
                bg="bg.muted"
                borderWidth="1px"
                borderColor="border.muted"
                minHeight="120px"
              >
                {isLoadingMessages ? (
                  <VStack
                    justifyContent="center"
                    alignItems="center"
                    height="100px"
                  >
                    <Text fontSize="sm" color="fg.muted">
                      {t('loadingMessages')}
                    </Text>
                  </VStack>
                ) : messages.length === 0 ? (
                  <VStack
                    justifyContent="center"
                    alignItems="center"
                    height="100px"
                  >
                    <Text fontSize="sm" color="fg.muted" textAlign="center">
                      {t('noMessages')}
                    </Text>
                  </VStack>
                ) : (
                  <VStack alignItems="stretch" gap={3}>
                    {messages.slice(0, 3).map((message) => (
                      <Box
                        key={message.id}
                        p={3}
                        bg="bg.default"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="border.muted"
                      >
                        <VStack alignItems="stretch" gap={2}>
                          <HStack
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <VStack alignItems="flex-start" gap={0.5}>
                              <Text fontSize="xs" color="fg.muted">
                                {new Date(message.receivedAt).toLocaleString()}
                              </Text>
                              {message.from && (
                                <Text
                                  fontSize="xs"
                                  color="fg.muted"
                                  fontFamily="mono"
                                >
                                  {t('from', { sender: message.from })}
                                </Text>
                              )}
                            </VStack>
                            <Clipboard.Root value={message.content}>
                              <Clipboard.Trigger asChild>
                                <IconButton variant="ghost" size="xs">
                                  <Clipboard.Indicator
                                    copied={<Tick01Icon size={12} />}
                                  >
                                    <Copy01Icon size={12} />
                                  </Clipboard.Indicator>
                                </IconButton>
                              </Clipboard.Trigger>
                            </Clipboard.Root>
                          </HStack>
                          <Text
                            fontSize="sm"
                            color="fg.default"
                            fontFamily="mono"
                            lineHeight="1.4"
                            maxHeight="120px"
                            overflowY="auto"
                            whiteSpace="pre-wrap"
                            wordBreak="break-word"
                          >
                            {message.content}
                          </Text>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            )}
          </VStack>
        </>
      )}
    </VStack>
  );
}

export type { WebhookDisplayProps };
